import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { t } from "@wr/i18n";
import { AuthCard, Button, CountdownAction, FormBanner, Mascot } from "@wr/ui";
import { AuthError, resendVerification, signOut } from "../lib/auth";
import { useLogout } from "../app/useLogout";
import { useOverlay } from "../app/OverlayProvider";
import { useSession } from "../app/SessionProvider";
import { destinationFor, sanitiseNext, stageFor, withNext } from "../app/routing";
import { fetchProfile, fetchSession } from "../lib/auth";

type State = "idle" | "resending" | "rate-limited";

/**
 * Verify email — design spec §7.
 *
 * **LOCKED: the 60s countdown starts on arrival, not on first tap**, because an
 * email was already sent by the time this screen renders. Starting it on tap
 * would let a player fire a second send one second after the first and then
 * wait a minute for the third.
 */
export function VerifyScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { session, reload } = useSession();
  const { showToast } = useOverlay();
  const logout = useLogout();

  const [state, setState] = useState<State>("idle");
  const [resendKey, setResendKey] = useState(0);
  const [checking, setChecking] = useState(false);

  // The session's own address is the truth; `?email=` only exists so the screen
  // can name the target immediately after a sign-up that has not been re-read.
  const email = session?.email || params.get("email") || "";
  const expired = params.get("error") !== null;
  const next = sanitiseNext(params.get("next"));

  async function resend() {
    setState("resending");
    try {
      /**
       * The screen's own `?next=` rides along (§14.2 item 5). This screen is
       * *inside* the interruption — it is the gate a deep-linked sign-up was
       * stopped at — so a mail sent from here promises the same landing as the
       * one that came with the sign-up.
       */
      await resendVerification(email, next);
      setState("idle");
      setResendKey((value) => value + 1);
      showToast(t("verify.resent"));
    } catch (caught) {
      if (caught instanceof AuthError && caught.status === 429) {
        setState("rate-limited");
        return;
      }
      setState("idle");
    }
  }

  /**
   * "I've verified — continue" re-reads the session and runs the same routing
   * matrix the splash does, rather than assuming success: the player may have
   * clicked the link in a different browser, in which case nothing changed here
   * and the honest answer is a toast, not a redirect.
   */
  async function recheck() {
    setChecking(true);
    try {
      const nextSession = await fetchSession();
      const nextProfile = nextSession ? await fetchProfile() : null;
      const stage = stageFor(nextSession, nextProfile);

      if (stage === "unverified") {
        showToast(t("verify.stillPending"));
        return;
      }
      await reload();
      void navigate(destinationFor(stage, next), { replace: true });
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="wr-auth-layout">
      <div className="wr-auth-layout__form">
        <div className="wr-form-column">
          <AuthCard testId="verify-card">
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Mascot size={72} />
            </div>

            <h1 className="wr-display-lg" data-route-heading>
              {t("verify.title")}
            </h1>
            <p className="wr-body-md wr-ink-70">{t("verify.body")}</p>
            {/* Its own mono line so a typo can be spotted (§7). */}
            <p className="wr-mono-md" data-testid="verify-email">
              {email}
            </p>

            {expired ? (
              <FormBanner tone="error" testId="verify-expired">
                {t("verify.linkExpired")}
              </FormBanner>
            ) : null}
            {state === "rate-limited" ? (
              <FormBanner tone="warning" testId="verify-rate-limited">
                {t("verify.rateLimited")}
              </FormBanner>
            ) : null}

            <CountdownAction
              key={resendKey}
              seconds={60}
              size="lg"
              fullWidth
              label={t("verify.resend")}
              waitingLabel={() => t("verify.resendWait")}
              loading={state === "resending"}
              loadingLabel={t("common.working")}
              onAction={() => void resend()}
              testId="verify-resend"
            />

            <Button
              variant="secondary"
              size="lg"
              fullWidth
              loading={checking}
              loadingLabel={t("common.working")}
              data-testid="verify-continue"
              onClick={() => void recheck()}
            >
              {t("verify.continue")}
            </Button>

            <Button
              variant="ghost"
              size="md"
              fullWidth
              data-testid="verify-change-email"
              onClick={() => {
                // Sign out first: the half-finished account still owns the
                // session, and /signup with a live session is a dead end.
                void signOut()
                  .catch(() => undefined)
                  .then(reload)
                  .then(() =>
                    navigate(withNext(`/signup?email=${encodeURIComponent(email)}`, next), {
                      replace: true,
                    }),
                  );
              }}
            >
              {t("verify.changeEmail")}
            </Button>

            {/*
              §14.1 ruling C, LOCKED. This signs out and leaves, which makes it
              a departure the player chose, so the `?next=` goes with it — bare
              `/login`. Contrast "Use a different email" above, which also signs
              out but is a *continuation* of the sign-up they are still in the
              middle of, and keeps the deep link it was carrying.
            */}
            <Button
              variant="ghost"
              size="md"
              fullWidth
              data-testid="verify-back"
              onClick={() => void logout()}
            >
              {t("common.backToLogin")}
            </Button>
          </AuthCard>
        </div>
      </div>
    </div>
  );
}
